/**
 * CategoryPicker — pixel-faithful port of the picker in
 * `design/project/screens-add.jsx`.
 *
 * Layout (matches the design source closely):
 *   ┌────────────────────────────────────────────────────────┐
 *   │ [食] [交] [住] [日] [娛] [醫] …                         │  <- horizontal pill tabs
 *   ├────────────────────────────────────────────────────────┤
 *   │ ┌──┐ ┌──┐ ┌──┐ ┌──┐ ┌──┐                              │
 *   │ │早│ │午│ │晚│ │飲│ │…│   <- 5-col sub-cat grid
 *   │ └──┘ └──┘ └──┘ └──┘ └──┘                              │
 *   └────────────────────────────────────────────────────────┘
 *
 * Filtering rules:
 *   - top tabs only show categories whose `kind` matches `props.kind` and
 *     whose `parent_id` is empty (i.e. main categories);
 *   - sub-grid shows children whose `parent_id === selectedId`.
 *
 * Sharp edges:
 *   - The picker is fully controlled. Don't try to "remember" the last
 *     selection inside; the parent (`AddTxScreen`) owns kind switches and
 *     resets `selectedId` accordingly.
 *   - When the active category's `color_token` is missing we fall back to
 *     `var(--cat-other)` rather than rendering an invisible chip.
 *   - Subcategory icon falls back to `tag` (matches the design source's
 *     `I[s.icon] || I.tag` pattern).
 */
import { useMemo } from 'react';
import type { CategoryRow, CategoryKind } from '@/lib/sheets/types';
import Icon, { type IconName } from '@/components/common/Icon';

export interface CategoryPickerProps {
  kind: CategoryKind;
  categories: CategoryRow[];
  selectedId: string | null;
  selectedSubId?: string | null;
  onChange: (categoryId: string, subId?: string) => void;
}

export default function CategoryPicker({
  kind,
  categories,
  selectedId,
  selectedSubId = null,
  onChange,
}: CategoryPickerProps): JSX.Element {
  // Main categories: parent_id empty + kind match + not soft-deleted.
  const mains = useMemo(
    () =>
      categories
        .filter((c) => !c.deleted_at && !c.parent_id && c.kind === kind)
        .sort((a, b) => a.sort_order - b.sort_order),
    [categories, kind],
  );

  const selected = useMemo(
    () => mains.find((c) => c.id === selectedId) ?? mains[0] ?? null,
    [mains, selectedId],
  );

  // Sub-categories of the active main.
  const subs = useMemo(() => {
    if (!selected) return [];
    return categories
      .filter((c) => !c.deleted_at && c.parent_id === selected.id)
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [categories, selected]);

  // The slice may store either bare token names (`--cat-food`) or full
  // `var(...)` literals — normalise to the var() form so all callers get
  // a valid CSS color expression. (This was the root cause of "sub-cat
  // icons invisible" — bare `--cat-food` is not valid CSS, so the icon
  // background div + stroke evaluated to nothing.)
  const rawColor = selected?.color_token ?? '--cat-other';
  const accentColor = rawColor.startsWith('--') ? `var(${rawColor})` : rawColor;
  // Soft 13%-alpha tint of the same color for the inactive sub-cat tile.
  // `color-mix` is supported in all evergreen browsers and is the cleanest
  // way to derive a translucent variant from a CSS variable.
  const accentSoft = `color-mix(in srgb, ${accentColor} 13%, transparent)`;

  return (
    <div data-testid="category-picker">
      {/* Main category — text-only pill tabs (horizontal scroll) */}
      <div
        style={{
          background: 'var(--cream-50)',
          borderBottom: '0.5px solid var(--border-1)',
        }}
      >
        <div
          className="no-scrollbar"
          style={{
            display: 'flex',
            gap: 6,
            padding: '10px 14px',
            overflowX: 'auto',
          }}
        >
          {mains.map((c) => {
            const on = selected?.id === c.id;
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={on}
                data-testid={`cat-tab-${c.id}`}
                onClick={() => onChange(c.id)}
                style={{
                  padding: '7px 14px',
                  borderRadius: 999,
                  flexShrink: 0,
                  background: on ? 'var(--mocha-700)' : 'transparent',
                  color: on ? 'var(--cream-50)' : 'var(--fg-2)',
                  border: on ? 0 : '0.5px solid var(--border-2)',
                  font: `${on ? 600 : 500} 13px var(--font-cjk)`,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sub-category grid (icon + label) */}
      {subs.length > 0 && (
        <div
          style={{
            padding: '12px 14px 8px',
            background: 'var(--cream-100)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 4,
            }}
          >
            {subs.map((s) => {
              const on = selectedSubId === s.id;
              const icon = (s.icon as IconName | undefined) ?? 'tag';
              return (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={on}
                  aria-label={s.name}
                  data-testid={`sub-${s.id}`}
                  onClick={() => selected && onChange(selected.id, s.id)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 5,
                    padding: '8px 2px 7px',
                    borderRadius: 12,
                    background: on ? 'var(--bg-surface)' : 'transparent',
                    border: on
                      ? '1px solid var(--border-2)'
                      : '1px solid transparent',
                    cursor: 'pointer',
                    boxShadow: on ? 'var(--shadow-1)' : 'none',
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background: on ? accentColor : accentSoft,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <Icon
                      name={icon}
                      size={16}
                      stroke={on ? '#fff' : accentColor}
                    />
                  </div>
                  <div
                    style={{
                      // Split shorthand `font` so React's runtime warning
                      // ("mixing shorthand + non-shorthand for the same
                      // property") goes away — `font:` sets line-height
                      // implicitly and conflicts with the explicit one.
                      fontFamily: 'var(--font-cjk)',
                      fontSize: '10.5px',
                      fontWeight: on ? 600 : 500,
                      color: on ? 'var(--fg-1)' : 'var(--fg-2)',
                      textAlign: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    {s.name}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
